/**
 * mapConfig.ts
 * Map size presets — stored in localStorage so the admin can switch sizes
 * for recording sessions without touching code. The game reads the active
 * preset at startup; changing it takes effect on the next new game.
 */

export interface MapSizePreset {
  readonly label:   string;
  readonly width:   number;
  readonly height:  number;
  readonly zoomMin: number;
  readonly description: string;
}

export const MAP_SIZE_PRESETS = {
  compact:  { label: 'Compact',  width:  80, height:  60, zoomMin: 0.35, description: 'Tight framing for close-up recordings' },
  standard: { label: 'Standard', width: 128, height:  96, zoomMin: 0.28, description: 'Original release size'                },
  extended: { label: 'Extended', width: 160, height: 120, zoomMin: 0.22, description: 'Larger map with more strategic depth'  },
  full:     { label: 'Full',     width: 192, height: 144, zoomMin: 0.18, description: 'Maximum size — shipping version'       },
} as const satisfies Record<string, MapSizePreset>;

export type MapSizeKey = keyof typeof MAP_SIZE_PRESETS;

const STORAGE_KEY = 'fb_map_size';

export function isMobileDevice(): boolean {
  return window.innerWidth < 768 || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
}

function defaultMapSizeKey(): MapSizeKey {
  return isMobileDevice() ? 'compact' : 'full';
}

export function getMapSize(): MapSizePreset {
  const saved = localStorage.getItem(STORAGE_KEY) as MapSizeKey | null;
  return MAP_SIZE_PRESETS[(saved && saved in MAP_SIZE_PRESETS ? saved : null) ?? defaultMapSizeKey()];
}

export function getMapSizeKey(): MapSizeKey {
  const saved = localStorage.getItem(STORAGE_KEY) as MapSizeKey | null;
  return (saved && saved in MAP_SIZE_PRESETS) ? saved : defaultMapSizeKey();
}

export function setMapSize(key: MapSizeKey): void {
  localStorage.setItem(STORAGE_KEY, key);
}
